import type { IconButtonProps } from '@mui/material/IconButton';

import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import MenuItem from '@mui/material/MenuItem';
import { useTheme, useColorScheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname } from 'src/routes/hooks';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { BaseOption } from 'src/components/settings/drawer/base-option';
import { useSettingsContext } from 'src/components/settings';
import Avatar from '@mui/material/Avatar';

import { nameToInitials, USER_INITIALS_AVATAR_BG } from 'src/utils/name-initials';

import { UpgradeBlock } from './nav-upgrade';
import { AccountButton } from './account-button';

import useAuth from '../../auth/useAuth';
import { useGetUserQuery } from 'src/generated/graphql';

// ----------------------------------------------------------------------

export type AccountDrawerProps = IconButtonProps & {
  data?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
  }[];
};

export function AccountDrawer({ data = [], sx, ...other }: AccountDrawerProps) {
  const { logout } = useAuth();

  const theme = useTheme();

  const settings = useSettingsContext();
  const { mode, setMode } = useColorScheme();

  const router = useRouter();

  const pathname = usePathname();

  const [open, setOpen] = useState(false);

  const [{ data: getUserData }] = useGetUserQuery();
  const _user = getUserData?.getUser;
  const displayName = _user?.username?.trim() || '';
  const drawerInitials = nameToInitials(displayName);

  const handleOpenDrawer = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClickItem = useCallback(
    (path: string) => {
      handleCloseDrawer();
      router.push(path);
    },
    [handleCloseDrawer, router]
  );

  return (
    <>
      <AccountButton
        onClick={handleOpenDrawer}
        displayName={displayName || 'User'}
        sx={sx}
        {...other}
      />

      <Drawer
        open={open}
        onClose={handleCloseDrawer}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 320, display: 'flex', flexDirection: 'column', maxHeight: '100%' } }}
      >
        <IconButton
          onClick={handleCloseDrawer}
          sx={{ top: 12, left: 12, zIndex: 9, position: 'absolute' }}
        >
          <Iconify icon="mingcute:close-line" />
        </IconButton>

        <Scrollbar sx={{ flex: '1 1 auto', minHeight: 0 }}>
          <Stack alignItems="center" sx={{ pt: 8 }}>
            <Avatar
              alt={displayName}
              sx={{
                width: 96,
                height: 96,
                fontSize: 36,
                fontWeight: 700,
                color: 'common.white',
                bgcolor: USER_INITIALS_AVATAR_BG,
              }}
            >
              {drawerInitials}
            </Avatar>

            <Typography variant="subtitle1" noWrap sx={{ mt: 2 }}>
              {_user?.username}
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, mb: 2.5 }} noWrap>
              {_user?.email}
            </Typography>
          </Stack>

          {/* <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" sx={{ p: 3 }}>
            {[...Array(3)].map((_, index) => (
              <Tooltip
                key={_mock.fullName(index + 1)}
                title={`Switch to: ${_mock.fullName(index + 1)}`}
              >
                <Avatar
                  alt={_mock.fullName(index + 1)}
                  src={_mock.image.avatar(index + 1)}
                  onClick={() => {}}
                />
              </Tooltip>
            ))}

            <Tooltip title="Add account">
              <IconButton
                sx={{
                  bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
                  border: `dashed 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.32)}`,
                }}
              >
                <Iconify icon="mingcute:add-line" />
              </IconButton>
            </Tooltip>
          </Stack> */}

          <Stack
            sx={{
              py: 3,
              px: 2.5,
              borderTop: `dashed 1px ${theme.vars.palette.divider}`,
              borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
            }}
          >
            {data.map((option) => {
              const rootLabel = pathname.includes('/dashboard') ? 'Home' : 'Dashboard';

              const rootHref = pathname.includes('/dashboard') ? '/' : paths.dashboard.root;

              return (
                <MenuItem
                  key={option.label}
                  onClick={() => handleClickItem(option.label === 'Home' ? rootHref : option.href)}
                  sx={{
                    py: 1,
                    color: 'text.secondary',
                    '& svg': { width: 24, height: 24 },
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  {option.icon}

                  <Box component="span" sx={{ ml: 2 }}>
                    {option.label === 'Home' ? rootLabel : option.label}
                  </Box>

                  {option.info && (
                    <Label color="error" sx={{ ml: 1 }}>
                      {option.info}
                    </Label>
                  )}
                </MenuItem>
              );
            })}
          </Stack>

          <Box sx={{ px: 2.5, py: 3 }}>
            <UpgradeBlock />
          </Box>
        </Scrollbar>

        <Box
          sx={{
            p: 2.5,
            flexShrink: 0,
            borderTop: (t) => `1px solid ${t.vars.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <BaseOption
            label="Dark mode"
            icon="moon"
            selected={settings.colorScheme === 'dark'}
            onClick={() => {
              settings.onUpdateField('colorScheme', mode === 'light' ? 'dark' : 'light');
              setMode(mode === 'light' ? 'dark' : 'light');
            }}
          />
          <Button
            onClick={async () => {
              await logout();
              handleCloseDrawer();
            }}
            size="large"
            variant="outlined"
            fullWidth
            sx={{
              borderColor: theme.palette.mode === 'light' ? 'black' : 'white',
              color: theme.palette.mode === 'light' ? 'black' : 'white',
            }}
          >
            Logout
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
